import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// Re-eksporter mutations + helpers så eksisterende imports virker uendret (VIB-653).
export * from "./use-upsell-mutations";

export type UpsellKind = "task" | "cost";
export type UpsellStatus = "draft" | "approved" | "deleted";
/** VIB-994: 'fixed' (fastpris, default) eller 'tm' (medgått tid + materiell). */
export type UpsellBillingMode = "fixed" | "tm";

export interface Upsell {
  id: string;
  project_id: string;
  description: string;
  amount_excl_vat: number;
  category: string | null;
  estimated_external_cost: number | null;
  allocated_hours: number | null;
  added_to_invoice_id: string | null;
  kind: UpsellKind;
  budget_line_id: string | null;
  source_voucher_id: string | null;
  created_at: string;
  // VIB-665: Godkjenningsflyt + per-upsell overhead/rabatt
  status: UpsellStatus;
  overhead_pct: number | null;
  discount_pct: number;
  discount_amount: number;
  approved_by: string | null;
  approved_at: string | null;
  // VIB-695-followup: Opplastet PDF/screenshot (i `offers` storage-bucket)
  attachment_path: string | null;
  attachment_filename: string | null;
  // VIB-994: 'fixed' (fastpris) eller 'tm' (medgått tid + materiell). Låses
  // (kan ikke endres) så snart mersalget har timeføring eller en faktura —
  // se src/lib/upsell-billing-mode-lock.ts.
  billing_mode: UpsellBillingMode;
}

export interface UpsellLine {
  id: string;
  phase: string | null;
  description: string | null;
  cost_type: string | null;
  unit: string | null;
  quantity: number | null;
  quantity_hours: number | null;
  amount: number | null;
  estimated_cost: number | null;
  upsell_id: string | null;
  template_id: string | null;
  /** VIB-2097: kr/t-basis linjen sist sto på i t/d. null = katalogens timesats. */
  rate_basis_hourly: number | null;
}

export interface UpsellWithLines extends Upsell {
  lines: UpsellLine[];
}

/**
 * Henter alle mersalg for et prosjekt og joiner underliggende budsjettlinjer
 * via budget_lines.upsell_id. Ett mersalg = én fakturalinje, men kan inneholde
 * flere oppgaver/tjenester.
 */
export function useUpsells(projectId: string | undefined) {
  return useQuery({
    queryKey: ["upsells", projectId],
    enabled: !!projectId,
    queryFn: async (): Promise<UpsellWithLines[]> => {
      // VIB-665: Filtrer ut soft-deleted upsells (status='deleted')
      const [upsellsRes, linesRes] = await Promise.all([
        supabase
          .from("upsells")
          .select("*")
          .eq("project_id", projectId!)
          .neq("status", "deleted")
          .order("created_at", { ascending: false }),
        supabase
          .from("budget_lines")
          .select("id, phase, description, cost_type, unit, quantity, quantity_hours, amount, estimated_cost, upsell_id, template_id, rate_basis_hourly")
          .eq("project_id", projectId!)
          .not("upsell_id", "is", null),
      ]);

      if (upsellsRes.error) throw upsellsRes.error;
      if (linesRes.error) throw linesRes.error;

      const linesByUpsell = new Map<string, UpsellLine[]>();
      for (const l of (linesRes.data ?? []) as UpsellLine[]) {
        if (!l.upsell_id) continue;
        const arr = linesByUpsell.get(l.upsell_id) ?? [];
        arr.push(l);
        linesByUpsell.set(l.upsell_id, arr);
      }

      return (upsellsRes.data ?? []).map((u) => ({
        ...(u as Upsell),
        lines: linesByUpsell.get(u.id) ?? [],
      }));
    },
  });
}
